import React, {useEffect, useState} from "react";
import {Checkbox, Dialog, Classes} from "@blueprintjs/core";
import {useTranslation} from "react-i18next";

// Components
import Button from "../common/components/button";
import TextInput from "../common/components/text-input";

// Actions
import {updateClient} from "./clients-actions";
import {useDispatch} from "react-redux";

// Hooks
import useInputValidation from "../common/hooks/use-input-validation";

// Libs
import {AVAILABLE_SERVICES} from "../services/services-config";

// Redux dispatch hook
const useReduxDispatch = () => {
    const dispatch = useDispatch();
    return {
        updateClient: (client) => dispatch(updateClient(client))
    }
};

const UpdateClientDialog = (props) => {
    const {isOpen, onClose, client} = props;
    const _id = client._id;

    // i18n
    const {t} = useTranslation();

    // Redux
    const {updateClient} = useReduxDispatch();

    // Form state
    const [nameData, setName] = useInputValidation();
    const [sapIdData, setSapId] = useInputValidation();
    const [nifData, setNif] = useInputValidation();
    const [services, setServices] = useState([]);

    // Set form state when client data changes
    useEffect(() => setFormData(client), [client]);

    const setFormData = client => {
        setName(client.name);
        setSapId(client.sap_id);
        setNif(client.nif);
        setServices(client.services || []);
    };

    const handleServiceToggle = (serviceName, checked) => {
        setServices(prev => {
            const existing = prev.find(s => s.service_name === serviceName);
            if (checked) {
                if (existing) {
                    return prev.map(s =>
                        s.service_name === serviceName ? { ...s, active: true } : s
                    );
                }
                return [...prev, {
                    service_name: serviceName,
                    active: true
                }];
            } else {
                return prev.map(s =>
                    s.service_name === serviceName ? { ...s, active: false } : s
                );
            }
        });
    };

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            lazy={true}
            style={{
                backgroundColor: "white",
                paddingBottom: "40px"
            }}
        >
            <div
                style={{margin: "20px 40px"}}
                className={Classes.DIALOG_BODY}
            >
                <h3 style={{margin: "20px 0"}}>{t("clients:editClientDialog.title")}</h3>
                <TextInput
                    label={t("clients:newClientDialog.input.name")}
                    value={nameData.value}
                    setValue={setName}
                    style={inputStyle}
                    placeholder={t("clients:newClientDialog.input.placeholder_name")}
                    validation
                    isValid={nameData.isValid}
                    errorMessage={nameData.errorMessage}
                />
                <TextInput
                    label={t("clients:newClientDialog.input.sap")}
                    value={sapIdData.value}
                    setValue={newValue => newValue.length <= 10 && setSapId(newValue)}
                    style={inputStyle}
                    placeholder={t("clients:newClientDialog.input.placeholder_sap")}
                    validation
                    isValid={sapIdData.isValid}
                    errorMessage={sapIdData.errorMessage}
                />
                <TextInput
                    label={t("clients:newClientDialog.input.nif")}
                    value={nifData.value}
                    setValue={setNif}
                    style={inputStyle}
                    placeholder={t("clients:newClientDialog.input.placeholder_nif")}
                    validation
                    isValid={nifData.isValid}
                    errorMessage={nifData.errorMessage}
                />
                <span style={{ fontSize: 14 }}>{t("clients:servicesSection.title")}</span>
                {AVAILABLE_SERVICES.map(svc => {
                    const serviceState = services.find(s => s.service_name === svc.service_name);
                    const isChecked = serviceState ? serviceState.active : false;
                    return (
                        <Checkbox
                            key={svc.service_name}
                            checked={isChecked}
                            label={t(`services:${svc.name}`)}
                            onChange={e => handleServiceToggle(svc.service_name, e.target.checked)}
                            style={{marginBottom: 8, marginTop: 8}}
                        />
                    );
                })}
            </div>
            <div
                style={{margin: "20px 40px 0 40px"}}
                className={Classes.DIALOG_FOOTER}>
                <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                    <Button
                        text={t("clients:editClientDialog.input.cancel")}
                        action={() => {
                            onClose();
                            setFormData(client);
                        }}
                        intent={"cancel"}
                        style={{
                            marginRight: "15px",
                        }}
                    />
                    <Button
                        text={t("clients:editClientDialog.input.save")}
                        action={() => {
                            updateClient({
                                _id,
                                name: nameData.value,
                                sap_id: sapIdData.value,
                                nif: nifData.value,
                                services: services
                            });
                            onClose();
                        }}
                        intent={"secondary"}
                        disabled={!(!nameData.isValid || !sapIdData.isValid || !nifData.isValid)}
                    />
                </div>
            </div>
        </Dialog>
    );
};

export default UpdateClientDialog;

const inputStyle = {
    marginBottom: "15px"
};